import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Detect if text contains Hebrew characters (Unicode range \u0590-\u05FF).
 */
export const isHebrew = (text) => /[\u0590-\u05FF]/.test(text || '');

/**
 * Get text direction: 'rtl' for Hebrew, 'ltr' otherwise.
 */
export const getTextDirection = (text) => (isHebrew(text) ? 'rtl' : 'ltr');

/*
Date Options:
  includeTime: boolean (default: false) - whether to include the time in the output (e.g., "Today, 14:30")
  useAlphabetDate: boolean (default: false) - whether to use an alphabetic date format (e.g., "Jan 1, 2020")
*/
export function formatDate(unixTimestamp, { includeTime = false, useAlphabetDate = false } = {}) {
  if (!unixTimestamp) return "DATE ERROR";

  const now = new Date();

  // we multiply by 1000 to convert seconds (from backend) to milliseconds (JS Date)
  unixTimestamp = unixTimestamp * 1000;
  const date = new Date(unixTimestamp); 

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);

  let datePart;

  // 1. Set the (relative) date string
  if (date >= startOfToday) {
    datePart = 'Today';
  } else if (date >= startOfYesterday) {
    datePart = 'Yesterday';
  } else if (!useAlphabetDate) {
    // Use en-GB locale for DD/MM/YYYY format
    datePart = date.toLocaleDateString('en-GB');
  } 
  // else if (date.getFullYear() === now.getFullYear()) {
  //   // Default format for this year (w/o showing the year)
  //   datePart = date.toLocaleDateString('en-US', {
  //     month: 'short',
  //     day: 'numeric',
  //   });
  // } 
  else {
    // Default format for previous years
    datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // 2. Return either the date string alone or with the time
  if (!includeTime) {
    return datePart;
  }

  const timeString = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart}, ${timeString}`;
}

const getGreeting = () => {
  return "Welcome";

};

export { getGreeting };


/*
  given a list of words, clean them by:
  1. lowercasing
  2. trimming punctuation
  3. removing stop words (English and Hebrew)
  4. removing empty strings

  [TODO: we didn't find an existing, satisfying logic/package that works for both English and Hebrew; so currently we use this basic approach]
*/
const cleanWords = (words) => {
  // English stop words 
  const baseWords = [
    'a', 'about', 'above', 'across', 'after', 'afterwards', 'again', 'against', 
    'all', 'almost', 'alone', 'along', 'already', 'also', 'although', 'always', 
    'am', 'among', 'amongst', 'amount', 'an', 'and', 'another', 'any', 'anyhow', 
    'anyone', 'anything', 'anyway', 'anywhere', 'are', 'around', 'as', 'at', 
    'back', 'be', 'became', 'because', 'become', 'becomes', 'becoming', 'been', 
    'before', 'beforehand', 'behind', 'being', 'below', 'beside', 'besides', 
    'between', 'beyond', 'both', 'bottom', 'but', 'by', 'call', 'can', 'cannot', 
    'ca', 'could', 'did', 'do', 'does', 'doing', 'done', 'down', 'due', 'during', 
    'each', 'eight', 'either', 'eleven', 'else', 'elsewhere', 'empty', 'enough', 
    'even', 'ever', 'every', 'everyone', 'everything', 'everywhere', 'except', 
    'few', 'fifteen', 'fifty', 'first', 'five', 'for', 'former', 'formerly', 
    'forty', 'four', 'from', 'front', 'full', 'further', 'get', 'give', 'go', 
    'had', 'has', 'have', 'he', 'hence', 'her', 'here', 'hereafter', 'hereby', 
    'herein', 'hereupon', 'hers', 'herself', 'him', 'himself', 'his', 'how', 
    'however', 'hundred', 'i', 'if', 'in', 'indeed', 'into', 'is', 'it', 'its', 
    'itself', 'just', 'keep', 'last', 'latter', 'latterly', 'least', 'less', 
    'made', 'make', 'many', 'may', 'me', 'meanwhile', 'might', 'mine', 'more', 
    'moreover', 'most', 'mostly', 'move', 'much', 'must', 'my', 'myself', 
    'name', 'namely', 'neither', 'never', 'nevertheless', 'next', 'nine', 'no', 
    'nobody', 'none', 'noone', 'nor', 'not', 'nothing', 'now', 'nowhere', 'of', 
    'off', 'often', 'on', 'once', 'one', 'only', 'onto', 'or', 'other', 
    'others', 'otherwise', 'our', 'ours', 'ourselves', 'out', 'over', 'own', 
    'part', 'per', 'perhaps', 'please', 'put', 'quite', 'rather', 're', 
    'really', 'regarding', 'same', 'say', 'see', 'seem', 'seemed', 'seeming', 
    'seems', 'serious', 'several', 'she', 'should', 'show', 'side', 'since', 
    'six', 'sixty', 'so', 'some', 'somehow', 'someone', 'something', 'sometime', 
    'sometimes', 'somewhere', 'still', 'such', 'take', 'ten', 'than', 'that', 
    'the', 'their', 'them', 'themselves', 'then', 'thence', 'there', 'thereafter', 
    'thereby', 'therefore', 'therein', 'thereupon', 'these', 'they', 'third', 
    'this', 'those', 'though', 'three', 'through', 'throughout', 'thru', 'thus', 
    'to', 'together', 'too', 'top', 'toward', 'towards', 'twelve', 'twenty', 
    'two', 'under', 'until', 'up', 'unless', 'upon', 'us', 'used', 'using', 
    'various', 'very', 'via', 'was', 'we', 'well', 'were', 'what', 
    'whatever', 'when', 'whence', 'whenever', 'where', 'whereafter', 'whereas', 
    'whereby', 'wherein', 'whereupon', 'wherever', 'whether', 'which', 'while', 
    'whither', 'who', 'whoever', 'whole', 'whom', 'whose', 'why', 'will', 'with', 
    'within', 'without', 'would', 'yet', 'you', 'your', 'yours', 'yourself', 
    'yourselves'
  ];

  // 2. Define the contractions
  const contractions = ["n't", "'d", "'ll", "'m", "'re", "'s", "'ve"];

  // 3. Create the final Set, starting with the base words and original contractions
  const engStopWords = new Set([...baseWords, ...contractions]);

  // 4. Generate and add variations with different apostrophes
  const apostrophes = ['‘', '’'];

  for (const apostrophe of apostrophes) {
      for (const stopword of contractions) {
          engStopWords.add(stopword.replace(/'/g, apostrophe));
      }
  }

    // Hebrew stop words [https://github.com/explosion/spaCy/blob/master/spacy/lang/he/stop_words.py]
  const hebStopWords = new Set([
     'אני', 'את', 'אתה', 'אנחנו', 'אתן', 'אתם', 'הם', 'הן', 'היא', 'הוא', 'שלי', 
    'שלו', 'שלך', 'שלה', 'שלנו', 'שלכם', 'שלכן', 'שלהם', 'שלהן', 'לי', 'לו', 
    'לה', 'לנו', 'לכם', 'לכן', 'להם', 'להן', 'אותה', 'אותו', 'זה', 'זאת', 'אלה', 
    'אלו', 'תחת', 'מתחת', 'מעל', 'בין', 'עם', 'עד', 'על', 'אל', 'מול', 'של', 
    'אצל', 'כמו', 'אחר', 'אותו', 'בלי', 'לפני', 'אחרי', 'מאחורי', 'עלי', 'עליו', 
    'עליה', 'עליך', 'עלינו', 'עליכם', 'עליכן', 'עליהם', 'עליהן', 'כל', 'כולם', 
    'כולן', 'כך', 'ככה', 'כזה', 'כזאת', 'זה', 'אותי', 'אותה', 'אותם', 'אותך', 
    'אותו', 'אותן', 'אותנו', 'ואת', 'את', 'אתכם', 'אתכן', 'איתי', 'איתו', 'איתך', 
    'איתה', 'איתם', 'איתן', 'איתנו', 'איתכם', 'איתכן', 'יהיה', 'תהיה', 'הייתי', 
    'היתה', 'היה', 'להיות', 'עצמי', 'עצמו', 'עצמה', 'עצמם', 'עצמן', 'עצמנו', 
    'מי', 'מה', 'איפה', 'היכן', 'במקום', 'שבו', 'אם', 'לאן', 'למקום', 'שבו', 
    'מקום', 'בו', 'איזה', 'מהיכן', 'איך', 'כיצד', 'באיזו', 'מידה', 'מתי', 
    'בשעה', 'ש', 'כאשר', 'כש', 'למרות', 'לפני', 'אחרי', 'מאיזו', 'סיבה', 
    'הסיבה', 'שבגללה', 'למה', 'מדוע', 'לאיזו', 'תכלית', 'כי', 'יש', 'אין', 
    'אך', 'מנין', 'מאין', 'מאיפה', 'יכל', 'יכלה', 'יכלו', 'יכול', 'יכולה', 
    'יכולים', 'יכולות', 'יוכלו', 'יוכל', 'מסוגל', 'לא', 'רק', 'אולי', 'אין', 
    'לאו', 'אי', 'כלל', 'בעד', 'נגד', 'אם', 'עם', 'אל', 'אלה', 'אלו', 'אף', 
    'על', 'מעל', 'מתחת', 'מצד', 'בשביל', 'לבין', 'באמצע', 'בתוך', 'דרך', 'מבעד', 
    'באמצעות', 'למעלה', 'למטה', 'מחוץ', 'מן', 'לעבר', 'מכאן', 'כאן', 'הנה', 'הרי', 
    'פה', 'שם', 'אך', 'ברם', 'שוב', 'אבל', 'מבלי', 'בלי', 'מלבד', 'רק', 'בגלל', 
    'מכיוון', 'עד', 'אשר', 'ואילו', 'למרות', 'כמו', 'כפי', 'אז', 'אחרי', 'כן', 
    'לכן', 'לפיכך', 'עז', 'מאוד', 'מעט', 'מעטים', 'במידה', 'שוב', 'יותר', 'מדי', 
    'גם', 'כן', 'נו', 'אחר', 'אחרת', 'אחרים', 'אחרות', 'אשר', 'או',
    'היו', 'היית', 'הייתה', 'היינו', 'הייתם', 'הייתן', 'היו',
  ]);

  const punctuation = new Set(['.', ',', '!', '?', ';', ':', '-', '_', '(', ')', '[', ']', '{', '}', '"', "'", '«', '»', '„', '“', '”', '‘', '’', '…']);

  // to lowercase
  words = words.map(w => w.toLowerCase());

  // clean punctuation
  words = words.map(w => w.trim());
  words = words.map(w => w.replace(/^[^\p{L}\p{N}_]+|[^\p{L}\p{N}_]+$/gu, ''))  // remove leading/trailing punctuation
  words = words.filter(w => w.length > 0 && !punctuation.has(w)); // remove empty strings and pure punctuation

  // clean stop words
  const stopWords = new Set([...engStopWords, ...hebStopWords]);
  words = words.filter(w => !stopWords.has(w)); // remove words containing stop words

  return words
};

export { cleanWords };

/**
 * Formats response text for better readability by:
 * 1. Detecting paragraph boundaries (double newlines, sentence endings)
 * 2. Splitting text into paragraphs while preserving source markers
 * 3. Handling bidirectional text detection (Hebrew vs English)
 * 
 * @param {string} text - The response text to format
 * @returns {Array<{text: string, hasHebrew: boolean}>} Array of paragraph objects
 */
export function formatResponseText(text) {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // Hebrew Unicode range: \u0590-\u05FF
  const hebrewRegex = /[\u0590-\u05FF]/;
  
  // Helper function to detect if text contains Hebrew
  const containsHebrew = (str) => hebrewRegex.test(str);

  // Split by double newlines first (explicit paragraph breaks)
  let paragraphs = text.split(/\n\n+/);
  
  // If no double newlines, try to detect paragraph boundaries by sentence endings
  if (paragraphs.length === 1) {
    // Split on single newlines that follow sentence endings
    // Look for: period/question/exclamation + space + newline + capital letter or Hebrew
    const sentenceEndPattern = /([.!?])\s*\n\s*([A-Z\u0590-\u05FF])/g;
    const singleNewlinePattern = /\n+/g;
    
    // First, try to split on sentence boundaries with newlines
    let tempText = text;
    tempText = tempText.replace(sentenceEndPattern, (match, punct, nextChar) => {
      return punct + '\n\n' + nextChar;
    });
    
    // Split on double newlines (now created or existing)
    paragraphs = tempText.split(/\n\n+/);
    
    // If still no paragraphs, split on single newlines as fallback
    if (paragraphs.length === 1 && singleNewlinePattern.test(text)) {
      paragraphs = text.split(/\n+/).filter(p => p.trim().length > 0);
    }
  }

  // Process each paragraph
  const formattedParagraphs = paragraphs
    .map(para => para.trim())
    .filter(para => para.length > 0)
    .map(para => ({
      text: para,
      hasHebrew: containsHebrew(para)
    }));

  // If we still have only one paragraph, try to split on sentence boundaries
  // This helps with long paragraphs that don't have explicit breaks
  if (formattedParagraphs.length === 1 && formattedParagraphs[0].text.length > 200) {
    const longText = formattedParagraphs[0].text;
    // Split on sentence endings followed by space and capital letter or Hebrew
    const sentenceBoundary = /([.!?])\s+([A-Z\u0590-\u05FF])/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = sentenceBoundary.exec(longText)) !== null) {
      const sentenceEnd = match.index + match[1].length;
      parts.push(longText.substring(lastIndex, sentenceEnd).trim());
      lastIndex = sentenceEnd;
    }
    
    // Add the remaining text
    if (lastIndex < longText.length) {
      parts.push(longText.substring(lastIndex).trim());
    }

    // Only split if we got meaningful parts
    if (parts.length > 1 && parts.every(p => p.length > 20)) {
      return parts
        .filter(p => p.length > 0)
        .map(p => ({
          text: p,
          hasHebrew: containsHebrew(p)
        }));
    }
  }

  return formattedParagraphs;
}